import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { PermissionAction } from "@shared/components/core/helpers/securityTypes"
import ConfigService from "@shared/helpers/configServices"

export interface User {
  firstName?: string
  lastName?: string
  fullName?: string
  name?: string
  userName: string
  employeeId: string
  uniqueId: string
  language: string
  avatar?: string
  email: string
  token: string
  accessToken?: string
  permissions: Permission[]
  specialAttributes?: string[]
}

export interface Permission {
  code: string
  action: PermissionAction
}

export const useUserStore = defineStore("user", () => {
  // State
  const user = ref<User>({
    firstName: "",
    lastName: "",
    fullName: "",
    userName: "",
    employeeId: "",
    uniqueId: "",
    avatar: "",
    email: "",
    token: "",
    accessToken: "",
    permissions: [],
    specialAttributes: [],
    language: "",
  })
  const storedUser = sessionStorage.getItem("JTP_User")

  if (storedUser) {
    Object.assign(user.value, JSON.parse(storedUser))
  }

  // Getters
  const isAuthenticated = computed(() => !!user.value.token)
  const getUser = computed(() => user.value)
  const isRegisteredInJtp = computed(() => !!user.value.uniqueId)
  const userLanguage = computed(() => user.value.language || ConfigService.Current.baseLanguage)

  // Actions
  function setUser(newUser: User) {
    user.value = newUser
    sessionStorage.setItem("JTP_User", JSON.stringify(user.value))
  }

  function setUserLanguage(language: string) {
    user.value.language = language
    sessionStorage.setItem("JTP_User", JSON.stringify(user.value))
  }

  function setUserAvatar(avatar: string | null) {
    user.value.avatar = avatar ?? undefined
    sessionStorage.setItem("JTP_User", JSON.stringify(user.value))
  }

  function logoutUser() {
    user.value = {
      email: "",
      token: "",
      userName: "",
      employeeId: "",
      uniqueId: "",
      firstName: "",
      lastName: "",
      fullName: "",
      language: ConfigService.Current.baseLanguage,
      permissions: [],
    }
    sessionStorage.removeItem("JTP_User")
  }

  return {
    user,
    userLanguage,
    isAuthenticated,
    isRegisteredInJtp,
    getUser,
    setUser,
    logoutUser,
    setUserLanguage,
    setUserAvatar,
    PermissionAction,
  }
})

export default useUserStore
